import { createMultiPartRequest, createRequest } from '../helpers/api_utils';
import {
  WellnessCandidate,
  WellnessCandidateResult,
  WellnessConfig,
  WellnessInvitation,
  WellnessSummaryResult,
  WellnessInvitationRequest
} from '../types/wellness_types';
import {apiUrl} from "./api_url";
import {JobRequisition} from "../types/JobOpenings";
import {sanitizeJobRequisition} from "../helpers/jobRequisitionUtils";

const apiEndpoint = apiUrl + '/wellness';

function mockResponse<T>(data: T, status = 200): Promise<Response> {
  return Promise.resolve(
    new Response(JSON.stringify(data), {
      status,
      headers: { 'Content-Type': 'application/json' },
    }),
  );
}

// ── Dummy data ───────────────────────────────────────────────────────────────

const DUMMY_CONFIG: WellnessConfig = {
  dayLimit: 7,
  types: ['DASS21', 'GAD7', 'PHQ9'],
};

const DUMMY_INVITATION: WellnessInvitation = {
  subject: 'Wellness Screening Invitation – {{job_title_name}}',
  body:
      '<p>Dear <strong>{{applicant_name}}</strong>,</p>' +
      '<p>You have been invited to participate in an online wellness screening by <strong>{{company_name}}</strong> for the <strong>{{job_title_name}}</strong> position.</p>' +
      '<p>Please complete the assessment by <strong>{{end_of_assessment}}</strong>.</p>' +
      '<p>Kindly note that the deadline is in Coordinated Universal Time (UTC). Please ensure that you take the time zone difference into account when submitting your response.</p>' +
      '<ol>' +
      '<li>Please open the email on a laptop. The wellness screening is not optimised for phones.</li>' +
      '<li>Press the Start Screening button.</li>' +
      '<li>Complete all screening questions honestly.</li>' +
      '<li>Close the browser tab when finished.</li>' +
      '</ol>' +
      '<p>We have created an account for you. To access the assessment, please click the button below:</p>' +
      '<p><a href="{{wellness_url}}" style="display:inline-block;padding:12px 24px;background-color:#7C3AED;color:#ffffff;text-decoration:none;border-radius:6px;font-weight:bold;font-size:16px;">Start Wellness Screening</a></p>' +
      '<p>If you have any questions or concerns, please don\'t hesitate to contact us at <a href="mailto:{{support_email}}">{{support_email}}</a>.</p>' +
      '<p>Thank you for your interest in <strong>{{company_name}}</strong>!</p>',
};

const DUMMY_CANDIDATES: WellnessCandidate[] = [
  { candidateId: '6a464ad9d906c9b3d09866cd', name: 'Bil Lal',   email: 'testhiacuity+400@gmail.com',  applicantStatus: 'Wellness',            invited: true,  assessmentStatus: 'In Progress' },
  { candidateId: '6a58f8fa85f6a5f2aa158ec7', name: 'James Martin',   email: 'testhiacuity+500@gmail.com',  applicantStatus: 'Wellness',            invited: true,  assessmentStatus: 'In Progress' },
  { candidateId: 'c-001', name: 'Mike Cook',   email: 'DemoUser2@hiacuity.com',  applicantStatus: 'Wellness',            invited: true,  assessmentStatus: 'In Progress' },
  { candidateId: 'c-002', name: 'Mike Cook',   email: 'DemoUser3@hiacuity.com',  applicantStatus: 'Auto-Assessment',     invited: true,  assessmentStatus: 'Completed'  },
  { candidateId: 'c-003', name: 'Mike Cook',   email: 'DemoUser4@hiacuity.com',  applicantStatus: 'Unsuccessful-Pending',invited: true,  assessmentStatus: 'Completed'  },
  { candidateId: 'c-004', name: 'Mike Cook',   email: 'DemoUser5@hiacuity.com',  applicantStatus: 'New',                 invited: false, assessmentStatus: 'Assigned'   },
  { candidateId: 'c-005', name: 'Mike Cook',   email: 'DemoUser6@hiacuity.com',  applicantStatus: 'New',                 invited: false, assessmentStatus: 'Assigned'   },
  { candidateId: 'c-006', name: 'Mike Cook',   email: 'DemoUser7@hiacuity.com',  applicantStatus: 'Wellness',            invited: true,  assessmentStatus: 'Completed'  },
  { candidateId: 'c-007', name: 'Mike Cook',   email: 'DemoUser10@hiacuity.com', applicantStatus: 'Wellness',            invited: true,  assessmentStatus: 'Completed'  },
  { candidateId: 'c-008', name: 'Mike Cook',   email: 'DemoUser11@hiacuity.com', applicantStatus: 'Wellness',            invited: true,  assessmentStatus: 'Completed'  },
  { candidateId: 'c-009', name: 'Sarah Patel', email: 'sarah.patel@example.com', applicantStatus: 'Wellness',            invited: true,  assessmentStatus: 'Completed'  },
  { candidateId: 'c-010', name: 'James Davis', email: 'james.davis@example.com', applicantStatus: 'Wellness',            invited: true,  assessmentStatus: 'Completed'  },
  { candidateId: 'c-011', name: 'Aisha Lee',   email: 'aisha.lee@example.com',   applicantStatus: 'Wellness',            invited: true,  assessmentStatus: 'Completed'  },
  { candidateId: 'c-012', name: 'Robert Taylor',email: 'robert.taylor@example.com',applicantStatus: 'New',              invited: true,  assessmentStatus: 'In Progress' },
];

const DUMMY_SUMMARY: WellnessSummaryResult = {
  assessments: [
    {
      type: 'DASS21',
      displayName: 'DASS-21',
      description: '(Depression, Anxiety, Stress)',
      completed: 32,
      pending: 5,
      bands: [
        { label: 'Normal',           count: 12, colorClass: 'bg-emerald-500' },
        { label: 'Mild',             count: 10, colorClass: 'bg-yellow-400'  },
        { label: 'Moderate',         count:  6, colorClass: 'bg-amber-500'   },
        { label: 'Severe',           count:  4, colorClass: 'bg-red-500'     },
        { label: 'Pending',          count:  5, colorClass: 'bg-slate-200'   },
      ],
    },
    {
      type: 'PHQ9',
      displayName: 'PHQ-9',
      description: '(Depression)',
      completed: 31,
      pending: 6,
      bands: [
        { label: 'Minimal',          count: 14, colorClass: 'bg-emerald-500' },
        { label: 'Mild',             count:  9, colorClass: 'bg-yellow-400'  },
        { label: 'Moderate',         count:  5, colorClass: 'bg-amber-500'   },
        { label: 'Severe',           count:  3, colorClass: 'bg-red-500'     },
        { label: 'Pending',          count:  6, colorClass: 'bg-slate-200'   },
      ],
    },
    {
      type: 'GAD7',
      displayName: 'GAD-7',
      description: '(Anxiety)',
      completed: 30,
      pending: 7,
      bands: [
        { label: 'Minimal',          count: 13, colorClass: 'bg-emerald-500' },
        { label: 'Mild',             count:  8, colorClass: 'bg-yellow-400'  },
        { label: 'Moderate',         count:  5, colorClass: 'bg-amber-500'   },
        { label: 'Severe',           count:  4, colorClass: 'bg-red-500'     },
        { label: 'Pending',          count:  7, colorClass: 'bg-slate-200'   },
      ],
    },
  ],
};

const DUMMY_CANDIDATE_RESULTS: WellnessCandidateResult[] = [
  {
    candidateId: 'r-001',
    name: 'Mike Cook',
    email: 'mike.cook@example.com',
    overallRisk: 'Mild',
    status: 'Completed',
    completedOn: '2025-05-25',
    dass21: {
      totalScore: 18, totalMax: 63, severity: 'Mild',
      completedDate: '2025-05-25',
      depressionScore: 7, depressionMax: 21, depressionSeverity: 'Mild',
      anxietyScore: 6,    anxietyMax: 21,    anxietySeverity: 'Mild',
      stressScore: 5,     stressMax: 21,     stressSeverity: 'Mild',
      questions: [
        { questionId: 'd1', text: 'I found it hard to wind down', score: 1, domain: 'Stress' },
        { questionId: 'd2', text: 'I was aware of dryness of my mouth', score: 2, domain: 'Anxiety' },
        { questionId: 'd3', text: 'I couldn\'t seem to experience any positive feeling at all', score: 1, domain: 'Depression' },
      ],
    },
    gad7: {
      totalScore: 9, totalMax: 21, severity: 'Mild', completedDate: '2025-05-25',
      questions: [
        { questionId: 'g1', text: 'Feeling nervous, anxious, or on edge', score: 2 },
        { questionId: 'g2', text: 'Not being able to stop or control worrying', score: 1 },
        { questionId: 'g3', text: 'Worrying too much about different things', score: 2 },
      ],
    },
    phq9: {
      totalScore: 7, totalMax: 27, severity: 'Mild', completedDate: '2025-05-25',
      questions: [
        { questionId: 'p1', text: 'Little interest or pleasure in doing things', score: 1 },
        { questionId: 'p2', text: 'Feeling down, depressed, or hopeless', score: 1 },
        { questionId: 'p3', text: 'Trouble falling or staying asleep', score: 2 },
      ],
    },
  },
  {
    candidateId: 'r-002',
    name: 'Sarah Patel',
    email: 'sarah.patel@example.com',
    overallRisk: 'Moderate',
    status: 'Completed',
    completedOn: '2025-05-24',
    dass21: {
      totalScore: 32, totalMax: 63, severity: 'Moderate',
      completedDate: '2025-05-24',
      depressionScore: 11, depressionMax: 21, depressionSeverity: 'Moderate',
      anxietyScore: 10,    anxietyMax: 21,    anxietySeverity: 'Moderate',
      stressScore: 11,     stressMax: 21,     stressSeverity: 'Moderate',
      questions: [
        { questionId: 'd1', text: 'I found it hard to wind down', score: 2, domain: 'Stress' },
        { questionId: 'd2', text: 'I was aware of dryness of my mouth', score: 2, domain: 'Anxiety' },
        { questionId: 'd3', text: 'I couldn\'t seem to experience any positive feeling at all', score: 2, domain: 'Depression' },
      ],
    },
    gad7: { totalScore: 11, totalMax: 21, severity: 'Moderate', completedDate: '2025-05-24', questions: [] },
    phq9: { totalScore: 14, totalMax: 27, severity: 'Moderate', completedDate: '2025-05-24', questions: [] },
  },
  {
    candidateId: 'r-003',
    name: 'James Davis',
    email: 'james.davis@example.com',
    overallRisk: 'High',
    status: 'Completed',
    completedOn: '2025-05-23',
    dass21: {
      totalScore: 41, totalMax: 63, severity: 'Severe',
      completedDate: '2025-05-23',
      depressionScore: 15, depressionMax: 21, depressionSeverity: 'Severe',
      anxietyScore: 13,    anxietyMax: 21,    anxietySeverity: 'Severe',
      stressScore: 13,     stressMax: 21,     stressSeverity: 'Severe',
      questions: [],
    },
    gad7: { totalScore: 17, totalMax: 21, severity: 'Severe',   completedDate: '2025-05-23', questions: [] },
    phq9: { totalScore: 19, totalMax: 27, severity: 'Severe',   completedDate: '2025-05-23', questions: [] },
  },
  {
    candidateId: 'r-004',
    name: 'Aisha Lee',
    email: 'aisha.lee@example.com',
    overallRisk: 'Low',
    status: 'Completed',
    completedOn: '2025-05-23',
    dass21: {
      totalScore: 9, totalMax: 63, severity: 'Normal',
      completedDate: '2025-05-23',
      depressionScore: 3, depressionMax: 21, depressionSeverity: 'Normal',
      anxietyScore: 3,    anxietyMax: 21,    anxietySeverity: 'Normal',
      stressScore: 3,     stressMax: 21,     stressSeverity: 'Normal',
      questions: [],
    },
    gad7: { totalScore: 3, totalMax: 21, severity: 'Minimal', completedDate: '2025-05-23', questions: [] },
    phq9: { totalScore: 4, totalMax: 27, severity: 'Minimal', completedDate: '2025-05-23', questions: [] },
  },
  {
    candidateId: 'r-005',
    name: 'Robert Taylor',
    email: 'robert.taylor@example.com',
    overallRisk: 'Mild',
    status: 'Invited',
    completedOn: undefined,
  },
];

// ── API functions ─────────────────────────────────────────────────────────────


// export function saveWellnessConfig(jobOpeningId: string, body: WellnessConfig): Promise<Response> {
//   void jobOpeningId;
//   return mockResponse({ ...DUMMY_CONFIG, ...body });
// }

export async function saveWellnessConfig(
    jobOpeningId: string,
    config: WellnessConfig,
) {
  return fetch(
      createRequest(
          apiEndpoint + `/${jobOpeningId}/configure`,
          'POST',
          'application/json',
          true,
          config,
      ),
  );
}


export function getWellnessConfig(jobOpeningId: string) {
  return fetch(createRequest(`${apiEndpoint}/${jobOpeningId}/configure`, 'GET', 'application/json', true));
}

export function getWellnessInvitation(jobOpeningId: string): Promise<Response> {
  void jobOpeningId;
  return mockResponse(DUMMY_INVITATION);
}

export function saveWellnessInvitation(jobOpeningId: string, body: WellnessInvitation): Promise<Response> {
  void jobOpeningId;
  return mockResponse({ ...DUMMY_INVITATION, ...body });
}

// export function getWellnessCandidates(jobOpeningId: string): Promise<Response> {
//   void jobOpeningId;
//   return mockResponse(DUMMY_CANDIDATES);
// }

// export function sendWellnessInvitation(jobOpeningId: string, candidateIds: string[]): Promise<Response> {
//   void jobOpeningId;
//   void candidateIds;
//   return mockResponse({ sent: candidateIds.length });
// }

export async function sendWellnessInvitation(
    jobOpeningId: string,
    invitation: WellnessInvitationRequest,
) {
  return fetch(
      createRequest(
          apiEndpoint + `/${jobOpeningId}/send-invite`,
          'POST',
          'application/json',
          true,
          invitation,
      ),
  );
}

export function getWellnessSummaryResults(jobOpeningId: string) {
  return fetch(createRequest(`${apiEndpoint}/${jobOpeningId}/results/summary`, 'GET', 'application/json', true));
}

export function getWellnessCandidateResults(jobOpeningId: string) {
  return fetch(createRequest(`${apiEndpoint}/${jobOpeningId}/results/candidates`, 'GET', 'application/json', true));
}


export type WellnessAnswer = {
  questionId: string;
  choiceAnswer: number; // 0-3
};

export type WellnessAnswerSubmission = {
  attemptId: string;
  answers: WellnessAnswer[];
};

export async function getNextAssessment(inviteId: string) {
  return fetch(
      createRequest(`${apiEndpoint}/invites/${inviteId}/next`, 'GET', 'application/json', true),
  );
}

export async function submitAnswer(submission: WellnessAnswerSubmission) {
  return fetch(
      createRequest(
          `${apiEndpoint}/attempts/${submission.attemptId}/answers`,
          'POST',
          'application/json',
          true,
          { answers: submission.answers }, // plain object, not stringified
      ),
  );
}



// Applicants tab (org side): candidates currently in the Wellness stage for a job opening.
export type WellnessApplicant = {
  id: string;
  name: string;
  email: string;
  status: string;
  invited: 'Yes' | 'No';
  assessmentStatus: string;
  invitationCount: number;
};

export type WellnessApplicantsResponse = {
  page: number;
  limit: number;
  total_count: number;
  results: WellnessApplicant[];
};

export function getWellnessCandidates(
    jobOpeningId: string,
    page: number = 1,
    limit: number = 10,
    search: string = '',
    emailSearch: string = '',
    assessmentStatus: string = '',
) {
  return fetch(
      createRequest(
          `${apiEndpoint}/${jobOpeningId}/applicants?page=${page}&limit=${limit}&search=${encodeURIComponent(search)}&email_search=${encodeURIComponent(emailSearch)}&assessment_status=${encodeURIComponent(assessmentStatus)}`,
          'GET',
          'application/json',
          true,
      ),
  );
}
